import { GeolocationService } from './geolocation.service';
import { hasActiveBoatPositionSource } from '../utils/boat-position-source.util';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { AccountContextService } from './account-context.service';
import { AuthService } from './auth.service';
import { EmergencyMessageService } from './emergency-message.service';
import { FirestoreService } from './firestore.service';
import { OnboardService } from './onboard.service';
import { Boat } from '../models/boat.model';

@Injectable({ providedIn: 'root' })
export class EmergencyContextPreloadService {
  private readonly destroyRef = inject(DestroyRef);
  private lastPositionRefreshAt = 0;
  private geolocationBlocked = false;

  constructor(
    private readonly authService: AuthService,
    private readonly accountContextService: AccountContextService,
    private readonly firestoreService: FirestoreService,
    private readonly onboardService: OnboardService,
    private readonly emergencyMessageService: EmergencyMessageService,
    private readonly geolocation: GeolocationService
  ) {
    this.preloadEmergencyContext();
  }

  private preloadEmergencyContext(): void {
    combineLatest([this.authService.user$, this.accountContextService.activeAccountId$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([user, accountId]) => {
          if (!user?.uid || !accountId) {
            return of(null);
          }

          return combineLatest([
            this.firestoreService.getBoats(accountId),
            this.onboardService.getOnboardPeople(accountId)
          ]).pipe(map(([boats, people]) => ({ accountId, boats, people })));
        })
      )
      .subscribe(payload => {
        if (!payload) {
          return;
        }

        const { accountId, boats, people: onboardPeople } = payload;
        const boat = this.selectCurrentBoat(boats ?? []);
        const peopleCount = Array.isArray(onboardPeople) ? onboardPeople.length : null;

        this.emergencyMessageService.saveBoatToCache(boat, accountId);
        this.emergencyMessageService.savePeopleOnBoardToCache(peopleCount);
        this.updatePositionCache();
      });
  }

  private updatePositionCache(): void {
    if (!navigator?.geolocation || this.geolocationBlocked) {
      return;
    }

    const now = Date.now();
    if (now - this.lastPositionRefreshAt < 60000) {
      return;
    }
    this.lastPositionRefreshAt = now;

    void this.updateBlockedPermissionState();

    navigator.geolocation.getCurrentPosition(
      position => {
        this.emergencyMessageService.savePositionToCache(
          this.formatPosition(position.coords.latitude, position.coords.longitude)
        );
      },
      error => {
        if (error?.code === error.PERMISSION_DENIED) {
          this.geolocationBlocked = true;
        }
        return;
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 12000
      }
    );
  }

  private async updateBlockedPermissionState(): Promise<void> {
    if (!('permissions' in navigator) || typeof navigator.permissions?.query !== 'function') {
      return;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      this.geolocationBlocked = permission.state === 'denied';
    } catch {
      return;
    }
  }

  private selectCurrentBoat(boats: Boat[]): Boat | null {
    if (!Array.isArray(boats) || !boats.length) {
      return null;
    }

    return boats.find(boat => !boat.deleted && hasActiveBoatPositionSource(boat) &&
      boat.positionSource?.deviceId === this.geolocation.deviceSource) ?? null;
  }

  private formatPosition(lat: number, lng: number): string {
    const latHemisphere = lat >= 0 ? 'N' : 'S';
    const lngHemisphere = lng >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(5)}° ${latHemisphere}, ${Math.abs(lng).toFixed(5)}° ${lngHemisphere}`;
  }
}
